import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, MagnifyingGlass, CircleNotch, ArrowRight, UserPlus, X,
  Users, Coins, EnvelopeSimple, CheckCircle, Warning, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });
}

// ─── Email Diagnostic Modal (v0.95.32) ─────────────────────────────
// Marcelo criou rep e email não chegou (Isaac 04/jul/2026). Este modal
// resolve auto-serviço:
//   1. GET /admin/email/health  → mostra qual provider está ativo,
//      se RESEND_API_KEY tem valor, prefix (nunca a chave inteira).
//   2. POST /admin/email/test   → envia email real usando o MESMO
//      pipeline que o fluxo do rep, mostra o retorno cru do Resend.
// Zero SSH, zero pedir pro dev. Em 30s o operador sabe se Resend está
// vivo ou se a env var sumiu do Railway.
function EmailDiagnosticModal({ onClose }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/admin/email/health")
      .then(({ data }) => setHealth(data))
      .catch(() => toast.error("Falha ao ler health do email."))
      .finally(() => setLoading(false));
  }, []);

  const runTest = async (e) => {
    e.preventDefault();
    if (!to || !to.includes("@")) {
      toast.error("Informe um email válido.");
      return;
    }
    setSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to: to.trim() });
      setTestResult(data);
      if (data.mode === "sent") {
        toast.success("Email enviado! Confira a caixa (e spam).");
      } else if (data.mode === "log_only") {
        toast.warning("RESEND_API_KEY vazio — caiu no LogOnly. Configure em Railway.");
      } else {
        toast.error(`Resend rejeitou: HTTP ${data.status || "?"}`);
      }
    } catch (err) {
      toast.error("Falha na requisição.");
    } finally {
      setSending(false);
    }
  };

  const isResend = health?.provider === "resend";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="email-diagnostic-modal">
      <div className="bg-slate-950 border border-white/10 rounded-xl w-full max-w-lg shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={16} weight="regular" className="text-slate-400" />
            <h2 className="text-white font-heading text-base font-semibold">Diagnóstico de email</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="email-diagnostic-close">
            <X size={16} weight="regular" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <CircleNotch size={14} weight="regular" className="animate-spin" />
              Lendo configuração…
            </div>
          ) : (
            <>
              <div className={`rounded-lg border p-4 ${
                isResend
                  ? "bg-emerald-500/[0.06] border-emerald-500/20"
                  : "bg-amber-500/[0.06] border-amber-500/20"
              }`}>
                <div className="flex items-start gap-2.5">
                  {isResend ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-400 mt-0.5" />
                  ) : (
                    <Warning size={16} weight="fill" className="text-amber-400 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-[13px] font-semibold ${isResend ? "text-emerald-200" : "text-amber-200"}`}>
                      {isResend ? "Resend ATIVO" : "Resend NÃO configurado (fallback LogOnly)"}
                    </div>
                    <div className="text-[12px] text-slate-400 mt-1 leading-relaxed">
                      {isResend
                        ? "Emails de credenciais, reset de senha e diagnósticos vão pela API do Resend."
                        : "Emails NÃO estão sendo enviados — só logados. Adicione RESEND_API_KEY nas env vars da produção (Railway) e faça redeploy."}
                    </div>
                  </div>
                </div>
                <dl className="grid grid-cols-[130px,1fr] gap-y-1.5 gap-x-3 mt-4 text-[12px]" data-testid="email-diagnostic-config">
                  <dt className="text-slate-500 uppercase tracking-[0.14em] text-[10px] font-medium">Provider</dt>
                  <dd className="text-white font-mono">{health.provider}</dd>
                  <dt className="text-slate-500 uppercase tracking-[0.14em] text-[10px] font-medium">API key</dt>
                  <dd className="text-white font-mono">
                    {health.api_key_configured
                      ? <>{health.api_key_prefix} <span className="text-slate-500">({health.api_key_length} chars)</span></>
                      : <span className="text-amber-300">vazio</span>}
                  </dd>
                  <dt className="text-slate-500 uppercase tracking-[0.14em] text-[10px] font-medium">From</dt>
                  <dd className="text-white font-mono truncate">{health.email_from || <span className="text-amber-300">não configurado</span>}</dd>
                  <dt className="text-slate-500 uppercase tracking-[0.14em] text-[10px] font-medium">From name</dt>
                  <dd className="text-white font-mono">{health.email_from_name || <span className="text-slate-500">—</span>}</dd>
                  <dt className="text-slate-500 uppercase tracking-[0.14em] text-[10px] font-medium">App URL</dt>
                  <dd className="text-white font-mono text-[11px] truncate">{health.app_public_url || <span className="text-slate-500">—</span>}</dd>
                </dl>
              </div>

              <form onSubmit={runTest} className="space-y-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-medium">Enviar teste real para</span>
                  <input
                    type="email"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="voce@exemplo.com"
                    required
                    disabled={sending}
                    data-testid="email-diagnostic-to"
                    className="mt-1.5 w-full bg-white/[0.04] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white font-mono placeholder:text-slate-600 focus:outline-none focus:border-white/30"
                  />
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="email-diagnostic-send"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-slate-900 rounded-md px-4 py-2.5 text-[13px] font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  {sending ? <CircleNotch size={14} weight="regular" className="animate-spin" /> : <PaperPlaneTilt size={14} weight="regular" />}
                  {sending ? "Enviando…" : "Enviar email de teste"}
                </button>
              </form>

              {testResult && (
                <div className={`rounded-lg border p-3 font-mono text-[11px] ${
                  testResult.mode === "sent"
                    ? "bg-emerald-500/[0.06] border-emerald-500/20 text-emerald-200"
                    : testResult.mode === "log_only"
                    ? "bg-amber-500/[0.06] border-amber-500/20 text-amber-200"
                    : "bg-rose-500/[0.06] border-rose-500/20 text-rose-200"
                }`} data-testid="email-diagnostic-result">
                  <div className="font-semibold mb-1">Resposta do provider:</div>
                  <pre className="whitespace-pre-wrap break-words leading-relaxed">{JSON.stringify(testResult, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function CreateRepModal({ onClose, onCreated, regionSuggestions = [] }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [slug, setSlug] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [region, setRegion] = useState("");
  const [phone, setPhone] = useState("");
  const [pct, setPct] = useState("20");
  const [saving, setSaving] = useState(false);

  // Auto-slug from name (kebab-case, ASCII only). Operator can override.
  const onNameChange = (v) => {
    setName(v);
    if (!slug || slug === slugify(name)) setSlug(slugify(v));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const pctNum = parseFloat(String(pct).replace(",", "."));
      if (isNaN(pctNum) || pctNum <= 0 || pctNum > 100) {
        toast.error("Comissão deve estar entre 0 e 100%.");
        setSaving(false);
        return;
      }
      const { data } = await api.post("/admin/representatives", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        slug: slug.trim().toLowerCase(),
        city: city.trim() || null,
        state: state.trim().toUpperCase() || null,
        region: region.trim() || null,
        phone: phone.trim() || null,
        default_commission_pct: pctNum / 100,
        status: "active",
      });
      toast.success(`Representante ${data.name} criado.`);
      onCreated(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao criar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0F0F11] border border-white/10 rounded-lg p-6 w-full max-w-lg" data-testid="rep-create-modal">
        <header className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Novo representante</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="rep-create-close">
            <X size={16} weight="regular" />
          </button>
        </header>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Nome" required>
            <input
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              required
              data-testid="rep-create-name"
              className={inputCls}
              placeholder="Marcelo Silva"
            />
          </Field>
          <Field label="Email" required>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="rep-create-email"
              className={inputCls}
              placeholder="marcelo@empresa.com"
            />
          </Field>
          <Field label="Slug (link público)" required hint="Aparece em /r/{slug}">
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              required
              data-testid="rep-create-slug"
              className={inputCls}
              placeholder="marcelo-rj"
              pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cidade">
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="rep-create-city"
                className={inputCls}
                placeholder="Rio de Janeiro"
              />
            </Field>
            <Field label="UF">
              <input
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                data-testid="rep-create-state"
                className={inputCls}
                placeholder="RJ"
                maxLength={2}
              />
            </Field>
          </div>
          <Field
            label="Região"
            hint="Agrupador comercial — ex.: Sudeste, Sul, Capital, Interior SP. Permite filtrar a carteira."
          >
            <input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              data-testid="rep-create-region"
              className={inputCls}
              placeholder="Sudeste"
              list="rep-region-suggestions"
            />
            {regionSuggestions.length > 0 && (
              <datalist id="rep-region-suggestions">
                {regionSuggestions.map((r) => <option key={r} value={r} />)}
              </datalist>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="rep-create-phone"
                className={inputCls}
                placeholder="21999998888"
              />
            </Field>
            <Field label="Comissão %" required>
              <input
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                required
                data-testid="rep-create-pct"
                className={inputCls}
                placeholder="20"
              />
            </Field>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="text-[13px] text-slate-400 hover:text-white px-3 py-2 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="rep-create-submit"
              className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 disabled:opacity-60 transition-colors"
            >
              {saving ? "Criando…" : "Criar representante"}
              {!saving && <UserPlus size={14} weight="regular" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-white/30 transition-colors";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </span>
      {children}
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </label>
  );
}

export default function AdminRepresentatives() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showEmailDiag, setShowEmailDiag] = useState(false);
  const navigate = useNavigate();

  const refresh = () => {
    setItems(null);
    api.get("/admin/representatives").then(({ data }) => setItems(data));
  };

  useEffect(() => { refresh(); }, []);

  // Unique regions across the carteira — sorted, blanks excluded.
  // Drives both the filter dropdown AND the suggestion datalist on the
  // create modal (so the operator reuses existing names, avoiding
  // typos like "Sudeste" vs "sudeste").
  const regions = useMemo(() => {
    if (!items) return [];
    const set = new Set();
    items.forEach((r) => {
      const v = (r.region || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return null;
    let list = items;
    if (regionFilter) {
      list = list.filter(
        (r) => (r.region || "").trim().toLowerCase() === regionFilter.toLowerCase(),
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.slug || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.city || "").toLowerCase().includes(q) ||
      (r.state || "").toLowerCase().includes(q) ||
      (r.region || "").toLowerCase().includes(q),
    );
  }, [items, search, regionFilter]);

  const totals = useMemo(() => {
    if (!items) return null;
    return items.reduce((acc, r) => ({
      clients:    acc.clients    + (r.total_clients || 0),
      active:     acc.active     + (r.active_clients || 0),
      pending:    acc.pending    + (r.commission_pending_cents || 0),
      liberable:  acc.liberable  + (r.commission_liberable_cents || 0),
      paid:       acc.paid       + (r.commission_paid_cents || 0),
    }), { clients: 0, active: 0, pending: 0, liberable: 0, paid: 0 });
  }, [items]);

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto" data-testid="admin-representatives-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
            Plataforma
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">
            Representantes regionais
          </h1>
          <p className="text-sm text-slate-400 mt-1.5">
            {items === null
              ? "Carregando…"
              : (
                <>
                  {items.length} {items.length === 1 ? "representante" : "representantes"}
                  {totals && (
                    <>
                      {" "}· {totals.active}/{totals.clients} clientes ativos · pendente{" "}
                      <span className="text-amber-300">{fmtBRL(totals.pending)}</span> · a liberar{" "}
                      <span className="text-emerald-400">{fmtBRL(totals.liberable)}</span> · pago{" "}
                      <span className="text-sky-300">{fmtBRL(totals.paid)}</span>
                    </>
                  )}
                </>
              )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEmailDiag(true)}
            data-testid="admin-email-diagnostic-btn"
            title="Diagnosticar envio de email (Resend)"
            className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 text-slate-200 px-3 py-2 rounded-md text-[13px] font-medium hover:bg-white/[0.08] hover:border-white/20 transition-colors"
          >
            <EnvelopeSimple size={14} weight="regular" /> Diagnosticar email
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="admin-rep-new-btn"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 transition-colors"
          >
            <Plus size={14} weight="regular" /> Novo representante
          </button>
        </div>
      </header>

      <div className="flex items-stretch gap-2 mb-4 flex-wrap">
        <div className="flex-1 min-w-[260px] bg-white/[0.03] border border-white/10 rounded-lg p-3 flex items-center gap-2">
          <MagnifyingGlass size={14} weight="regular" className="text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, slug, email, cidade, UF ou região…"
            data-testid="admin-rep-search"
            className="bg-transparent flex-1 text-sm text-white placeholder:text-slate-600 focus:outline-none"
          />
        </div>
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          data-testid="admin-rep-region-filter"
          className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30 transition-colors min-w-[180px]"
        >
          <option value="">Todas as regiões{regions.length > 0 ? ` (${regions.length})` : ""}</option>
          {regions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        {(search || regionFilter) && (
          <button
            type="button"
            onClick={() => { setSearch(""); setRegionFilter(""); }}
            data-testid="admin-rep-clear-filters"
            className="text-[12px] text-slate-400 hover:text-white border border-white/10 hover:border-white/30 rounded-lg px-3 transition-colors"
          >
            limpar
          </button>
        )}
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-white/[0.02] text-left">
            <tr>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Nome</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Slug</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Região</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Status</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Clientes</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Pendentes</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">A liberar</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Pagas</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered === null && (
              <tr><td colSpan="9" className="p-12 text-center">
                <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
              </td></tr>
            )}
            {filtered?.length === 0 && (
              <tr><td colSpan="9" className="p-12 text-center text-slate-500 text-sm">
                <Users size={20} weight="regular" className="inline-block mb-2 text-slate-600" />
                <div>
                  {items?.length === 0
                    ? "Nenhum representante cadastrado ainda."
                    : "Nenhum representante encontrado com este filtro."}
                </div>
              </td></tr>
            )}
            {filtered?.map((r) => (
              <tr
                key={r.id}
                onClick={() => navigate(`/admin-master/representantes/${r.id}`)}
                data-testid={`rep-row-${r.id}`}
                className="border-t border-white/5 hover:bg-white/[0.04] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="text-white font-medium">{r.name}</div>
                  <div className="text-[11px] text-slate-500">{r.email}</div>
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-emerald-300">/r/{r.slug}</td>
                <td className="px-4 py-3 text-[12px] text-slate-300">
                  {[r.city, r.state].filter(Boolean).join(" / ") || r.region || "—"}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${
                    r.status === "active"
                      ? "bg-emerald-500/10 text-emerald-300"
                      : "bg-slate-500/10 text-slate-400"
                  }`}>
                    {r.status === "active" ? "ativo" : "inativo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-slate-300 tabular-nums">
                  {r.active_clients}/{r.total_clients}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-amber-300">
                  {fmtBRL(r.commission_pending_cents)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-400">
                  {fmtBRL(r.commission_liberable_cents)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-sky-300">
                  {fmtBRL(r.commission_paid_cents)}
                </td>
                <td className="px-4 py-3">
                  <ArrowRight size={12} weight="regular" className="text-slate-600" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateRepModal
          regionSuggestions={regions}
          onClose={() => setShowCreate(false)}
          onCreated={(rep) => {
            setShowCreate(false);
            refresh();
            navigate(`/admin-master/representantes/${rep.id}`);
          }}
        />
      )}

      {showEmailDiag && (
        <EmailDiagnosticModal onClose={() => setShowEmailDiag(false)} />
      )}
    </div>
  );
}

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 60);
}
